import { useState } from 'react';
import type { ReactElement } from 'react';

import { useApp } from '@weke/ui';
import { terminal } from '../app/terminal.js';
import { formatMoney, priceDigits } from '@weke/ui';

/**
 * The navigator: what this account can reach, as a tree.
 *
 * The entries are the things that exist — the account, the symbol catalogue, the
 * order vocabulary, the layouts. No Indicators or Expert Advisors branch: this
 * build has neither, and a tree full of dead leaves teaches a trader to ignore
 * the tree.
 */
export function Navigator(): ReactElement {
  const app = useApp();
  const [tab, setTab] = useState<'common' | 'favorites'>('common');
  const [open, setOpen] = useState<Record<string, boolean>>({
    accounts: true,
    symbols: true,
    trading: false,
    layouts: false,
  });

  const toggle = (key: string): void => setOpen((current) => ({ ...current, [key]: !current[key] }));
  const section = (key: string, label: string, children: ReactElement[]): ReactElement => (
    <div key={key}>
      <div
        className="tree-row"
        role="treeitem"
        aria-expanded={open[key] === true}
        tabIndex={0}
        onClick={() => toggle(key)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') toggle(key);
        }}
      >
        <span className="faint">{open[key] === true ? '▾' : '▸'}</span>
        <span>{label}</span>
      </div>
      {open[key] === true ? <div className="pl-4">{children}</div> : null}
    </div>
  );

  const symbols = tab === 'favorites' ? app.symbols.slice(0, 1) : app.symbols;

  return (
    <section className="panel min-h-0 flex-1" aria-label="Navigator">
      <header className="panel-head">
        <span>Navigator</span>
        <span className="faint">{app.symbols.length + 3}</span>
      </header>
      <div className="panel-body py-1" role="tree" aria-label="Navigator tree">
        {section('accounts', 'Accounts', [
          <div
            key="account"
            className="tree-row"
            role="treeitem"
            aria-selected={true}
            tabIndex={0}
            title={app.account?.account_id ?? ''}
          >
            <span className="text-muted">▣</span>
            <span>
              {app.account === null
                ? '—'
                : `${app.account.login} (${app.account.currency})`}
            </span>
            <span className="ml-auto pr-1 text-2xs faint">
              {app.account === null
                ? ''
                : formatMoney(app.account.balance_minor, app.account.digits)}
            </span>
          </div>,
        ])}
        {section(
          'symbols',
          `Symbols`,
          symbols.map((symbol) => (
            <div
              key={symbol.name}
              className="tree-row"
              role="treeitem"
              aria-selected={app.selectedSymbol === symbol.name}
              tabIndex={0}
              data-testid={`nav-symbol-${symbol.name}`}
              onClick={() => terminal.selectSymbol(symbol.name)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') terminal.selectSymbol(symbol.name);
              }}
            >
              <span className="text-muted">•</span>
              <span>{symbol.name}</span>
              <span className="ml-auto pr-1 text-2xs faint">
                {priceDigits(symbol.digits, symbol.tick_size_e8)}
              </span>
            </div>
          )),
        )}
        {section('trading', 'Order types', [
          ...['Market', 'Limit', 'Stop', 'Stop limit'].map((label) => (
            <div key={label} className="tree-row" role="treeitem" tabIndex={-1}>
              <span className="text-muted">·</span>
              <span>{label}</span>
            </div>
          )),
        ])}
        {section('layouts', 'Saved layouts', [
          <div
            key="default"
            className="tree-row"
            role="treeitem"
            aria-selected={true}
            tabIndex={0}
          >
            <span className="text-muted">▤</span>
            <span>Default</span>
          </div>,
        ])}
      </div>
      <div className="tabs" role="tablist" aria-label="Navigator tabs">
        {(['common', 'favorites'] as const).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            className="tab capitalize"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </div>
    </section>
  );
}
